import { Network } from '@capacitor/network';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';

import { IS_ANDROID, IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

import { timeout } from './common-utils';

const DEFAULT_RECONNECT_MAX_ATTEMPTS = 6;
const DEFAULT_RECONNECT_DELAY_MS = 2000;

export async function canProceedPastReconnectGate(evseSsid?: string): Promise<boolean> {
  if (IS_NATIVE && evseSsid) {
    try {
      const ssidAfterConnect = await CapacitorWifiConnect.getDeviceSSID();
      const currentSsid = ssidAfterConnect?.value;

      // Device is still connected to the EV Charger Wi-Fi
      if (currentSsid === evseSsid) {
        return false;
      }

      // On Android the plugin binds the app process to the requested network.
      // If the user manually switches away from the EVSE AP in system settings,
      // we still need to release that app-level binding so internet checks see
      // the real active network again.
      if (IS_ANDROID) {
        await CapacitorWifiConnect.disconnect().catch(() => undefined);
      }
    } catch {
      return false;
    }
  }

  const networkStatus = await Network.getStatus();
  // Device is not connected to a Wi-Fi network with internet access
  if (!networkStatus.connected) {
    return false;
  }

  return true;
}

export async function waitForReconnectGate(
  evseSsid?: string,
  maxAttempts = DEFAULT_RECONNECT_MAX_ATTEMPTS,
  delayMs = DEFAULT_RECONNECT_DELAY_MS
): Promise<boolean> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const canContinue = await canProceedPastReconnectGate(evseSsid);
    if (canContinue) {
      return true;
    }

    if (attempt < maxAttempts) {
      await timeout(delayMs);
    }
  }

  return false;
}
